import styled from "styled-components";
import { Icon } from "js/Components/Icon";
import React, { useState } from "react";
import { Button } from "js/Components/Button";
import { themeColors } from "js/react/sharedStyles";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { logger } from "js/react/views/UserOptions/panes/BillingPane/Components/PaymentMethodBlock";
import { Input } from "js/Components/Input";
import { workspaces as workspacesApi } from "apis/callables";
import type StripeTypes from "stripe";

const PromoContainer = styled.div`
    width: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    margin-top: 10px;
`;

const PromoInputContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`;

const PromoInput = styled(Input)`
    width: 100%;
`;

const PromoTerms = styled.div`
    font-size: 14px;
    margin-top: 5px;
    color: #333;
    font-weight: 600;

    > a {
        text-decoration: none;
        color: ${themeColors.ui_blue};
    }
`;

const PromoError = styled.div`
    font-size: 14px;
    margin-top: 5px;
    color: ${themeColors.warning};
    font-weight: 600;
`;

const StyledPromoCoupon = styled.div`
    display: flex;
    flex-flow: row;
    gap: 10px;
    font-size: 15px;
    text-transform: uppercase;
    font-weight: 900;
    color: #333;

    .bai-icon {
        font-size: 22.5px;
        margin-top: -1px;
    }
`;

export interface IPromoFormProps {
    verifyPromoCode: (promoCode: StripeTypes.PromotionCode) => Promise<void>;
}

export const PromoForm = (props: IPromoFormProps) => {
    const [isAddingPromo, setIsAddingPromo] = useState(false);
    const [isApplyingPromo, setIsApplyingPromo] = useState(false);
    const [promoError, setPromoError] = useState(null);
    const [promoCode, setPromoCode] = useState("");

    const handleAddPromo = () => {
        setIsAddingPromo(true);
    };

    const handleChangePromoCode = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newCode = e.target.value;

        setPromoCode(newCode);
        setPromoError(null);

        if (promoCode !== "" && newCode === "") {
            setIsAddingPromo(false);
        }
    };

    const handleApplyPromo = async () => {
        setIsApplyingPromo(true);

        try {
            const promotionCode = await workspacesApi.getPromotionCode({ promotionCode: promoCode });
            await props.verifyPromoCode(promotionCode);
        } catch (err) {
            logger.error(err, `[PromoForm] handleApplyPromo() failed`);
            setPromoError(err.message);
        } finally {
            setIsApplyingPromo(false);
        }
    };

    if (!isAddingPromo) {
        return (<PromoContainer data-test-id="promo-container">
            <Button unframed large color={themeColors.ui_blue} onClick={handleAddPromo}>add promo code</Button>
        </PromoContainer>);
    }

    return (<>
        <PromoContainer data-test-id="promo-container">
            <FetchingClickShield visible={isApplyingPromo} backgroundColor="rgba(255,255,255,0.7)"/>
            <PromoInputContainer>
                <PromoInput width="100%" value={promoCode} onChange={handleChangePromoCode} placeholder="Promo Code"/>
                <Button unframed large color={themeColors.ui_blue} onClick={handleApplyPromo}
                    disabled={!promoCode || promoError || isApplyingPromo}>apply</Button>
            </PromoInputContainer>
            {!promoError && <PromoTerms>
                By clicking apply you agree to the{" "}
                <a
                    href="/promotion-terms"
                    target="_blank"
                >Promotion Terms and Conditions
                </a>
            </PromoTerms>}
            {promoError && <PromoError>{promoError}</PromoError>}
        </PromoContainer>
    </>);
};

export const PromoCoupon = (props: {coupon: StripeTypes.Coupon})=> {
    const { coupon } = props;

    return (<PromoContainer data-test-id="promo-container">
        <StyledPromoCoupon>
            <Icon color="rgb(143, 177, 48)">confirmation_number</Icon>
            {coupon.name}{" "}
            ({[
                coupon.amount_off ? `$${coupon.amount_off / 100}` : `${coupon.percent_off}%`,
                "off",
                coupon.duration === "repeating" ? `for ${coupon.duration_in_months} months` : coupon.duration
            ].join(" ")})
        </StyledPromoCoupon>
    </PromoContainer>);
};
